import { Locales } from '@griply/types';
import React from 'react';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { ID } from 'shared/types/id';
import { Person } from 'shared/types/nlp';
import { parseTarget } from 'shared/utils/nlp/frequency-processing';
import { getDisplayDate } from 'shared/utils/nlp/get-display-date';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { getWordIndexFromMatch } from 'shared/utils/nlp/get-word-index-for-match';
import { isInsideIgnoredRange } from 'shared/utils/nlp/inside-ignored-range';
import { parseRrule } from 'shared/utils/nlp/rrule-processing';

import { getCursorPosition } from './get-cursor-position';
import { Styles } from './get-styles';

export type Options = {
  inputRef: React.RefObject<HTMLDivElement>;
  language: Locales;
  dateFormat: DateFormatOptions;
  MASTER_REGEX_G: RegExp;
  wordIds: WordId[];
  ignoredWordIds: ID[];
  createHabitAllowed: boolean;
  createTargetHabitAllowed: boolean;
  autocomplete: {
    show: boolean;
    trigger: string | null;
    startIndex: number;
    textToReplace: string;
  };
  people?: Person[];
  desiredCursorPos: React.MutableRefObject<number | null>;
  onHighlightedWord: (wordId: ID) => void;
  styles: Styles;
};

// This effect is responsible for rendering the highlighted text inside the content-editable div.
// It replaces plain text with styled <span> elements for detected keywords.
export const renderHighlightedText = (
  value: string,
  {
    inputRef,
    language,
    dateFormat,
    MASTER_REGEX_G,
    wordIds,
    ignoredWordIds,
    createHabitAllowed,
    createTargetHabitAllowed,
    autocomplete,
    desiredCursorPos,
    onHighlightedWord,
    styles,
  }: Options,
) => {
  const currentCursorPos = desiredCursorPos.current;
  desiredCursorPos.current = null;

  if (!inputRef.current) {
    return;
  }
  inputRef.current.innerHTML = '';
  let lastIndex = 0;

  // Get all possible matches using the master regex ONE time.
  const allMatches = [...value.matchAll(MASTER_REGEX_G)];
  const validMatches = allMatches.filter(
    (m) => !isInsideIgnoredRange(m, { wordIds, ignoredWordIds }),
  );

  // Find the single "winning" schedule item (the one that appears last in the string).
  const scheduleCandidates = [
    validMatches
      .filter(
        (m) =>
          m.groups?.deadline &&
          getDisplayDate(
            m.groups?.deadline.slice(1, -1),
            language,
            dateFormat,
            true,
          ),
      )
      .pop(), // Deadline
    createHabitAllowed
      ? validMatches
          .filter(
            (m) => m.groups?.rrule && parseRrule(m.groups.rrule, language),
          )
          .pop()
      : null, // Rrule
    createHabitAllowed && createTargetHabitAllowed
      ? validMatches
          .filter(
            (m) => m.groups?.target && parseTarget(m.groups.target, language),
          )
          .pop()
      : null, // Target
  ].filter(Boolean); // Remove any nulls

  scheduleCandidates.sort((a, b) => (b?.index ?? -1) - (a?.index ?? -1));
  const winningScheduleMatch = scheduleCandidates.length
    ? scheduleCandidates[0]
    : null;

  // Find the winning due date. It must NOT be inside the winning schedule match.
  const lastDateMatch = validMatches
    .filter((m) => {
      if (!m.groups?.dueDate) {
        return false;
      } // Must be a due date match
      // If a schedule item won, ensure this date is not part of it.
      if (
        winningScheduleMatch &&
        (m.index ?? -1) >= (winningScheduleMatch.index ?? -1) &&
        (m.index ?? -1) + m[0].length <=
          (winningScheduleMatch.index ?? -1) + winningScheduleMatch[0].length
      ) {
        return false;
      }
      return getDisplayDate(m.groups.dueDate, language, dateFormat, false);
    })
    .pop();

  // Unconditionally find all other items.
  const activeGoals = allMatches.filter(
    (m) =>
      m.groups?.linkedItem &&
      !isInsideIgnoredRange(m, { wordIds, ignoredWordIds }),
  );
  const activeTags = allMatches.filter(
    (m) =>
      m.groups?.tag && !isInsideIgnoredRange(m, { wordIds, ignoredWordIds }),
  );
  const activePeople = allMatches.filter(
    (m) =>
      m.groups?.person && !isInsideIgnoredRange(m, { wordIds, ignoredWordIds }),
  );
  const lastPriorityMatch = allMatches
    .filter(
      (m) =>
        m.groups?.priority &&
        !isInsideIgnoredRange(m, { wordIds, ignoredWordIds }),
    )
    .pop();

  // Find the last instance of each type of time property.
  const timeCandidates = [
    validMatches.filter((m) => m.groups?.timeRange).pop(), // Time Range
    validMatches.filter((m) => m.groups?.singleTime).pop(), // Single Time
    validMatches.filter((m) => m.groups?.singleTimeAt).pop(), // "at" Time
  ].filter(Boolean);

  timeCandidates.sort((a, b) => (b?.index ?? -1) - (b?.index ?? -1));

  const lastActiveTimeMatch = timeCandidates.length ? timeCandidates[0] : null;

  const lastDurationMatch = validMatches
    .filter(
      (m) =>
        m.groups?.duration &&
        (!lastActiveTimeMatch || !lastActiveTimeMatch.groups?.timeRange),
    )
    .pop(); // Ignore duration if a time range exists
  const lastReminderMatch = allMatches
    .filter(
      (m) =>
        m.groups?.reminder &&
        !isInsideIgnoredRange(m, { wordIds, ignoredWordIds }),
    )
    .pop();

  // 5. Assemble the final, correct list of all keywords to be highlighted
  const activeKeywordMatches = [
    winningScheduleMatch,
    lastDateMatch,
    lastPriorityMatch,
    lastActiveTimeMatch,
    lastDurationMatch,
    lastReminderMatch,
    ...activeGoals,
    ...activeTags,
    ...activePeople,
  ].filter(Boolean); // Filter out any null/undefined results

  // This logic handles the autocomplete suggestion text and prevents it from being highlighted while typing.
  let autocompleteTermInfo: {
    term: string | null;
    triggerIndex: number;
    trigger: string | null;
  } = { term: null, triggerIndex: -1, trigger: null };
  // We only perform this check if the autocomplete dropdown is actually visible.
  // This prevents a selected item from being incorrectly identified as "in-progress" right after selection.
  if (autocomplete.show && inputRef.current) {
    const cursorPosition = currentCursorPos ?? 0;
    const textBeforeCursor = value.substring(0, cursorPosition);

    // Instead of re-calculating the term, we now rely on the autocomplete state as the source of truth.
    // This ensures the highlighting logic is perfectly in sync with the input handler.
    const term = textBeforeCursor.substring(autocomplete.startIndex);
    autocompleteTermInfo = {
      term,
      triggerIndex: autocomplete.startIndex,
      trigger: autocomplete.trigger,
    };
  }

  // Sort the keywords from the state so we can render them in order.
  const sortedKeywords = [...activeKeywordMatches].sort(
    (a, b) => (a?.index ?? -1) - (b?.index ?? -1),
  );

  for (const match of sortedKeywords) {
    if (!match) {
      continue;
    }
    // Add the plain text that comes before this keyword.
    const plainText = value.substring(lastIndex, match.index);
    if (plainText) {
      inputRef.current.appendChild(document.createTextNode(plainText));
    }

    const fullMatch = match[0];

    // This logic prevents highlighting the text currently being typed for autocomplete.
    if (
      autocompleteTermInfo.trigger &&
      match.index === autocompleteTermInfo.triggerIndex &&
      autocomplete.textToReplace.startsWith(fullMatch)
    ) {
      inputRef.current.appendChild(document.createTextNode(fullMatch));
      lastIndex = match.index + fullMatch.length;
      continue;
    }

    const span = document.createElement('span');
    span.innerText = fullMatch;

    // Determine the style based on which capture group in the match object has content.
    let styleToApply = {};

    if (match.groups?.linkedItem) {
      styleToApply = styles.goalSpanInInput;
    } else if (match.groups?.tag) {
      styleToApply = styles.tagSpanInInput;
    } else if (match.groups?.person) {
      styleToApply = styles.personSpanInInput;
    } else if (match.groups?.priority) {
      const pKeyword = match.groups.priority.toLowerCase();
      if (pKeyword === 'p1' || pKeyword === '!!1') {
        styleToApply = styles.prioritySpanInInputHigh;
      } else if (pKeyword === 'p2' || pKeyword === '!!2') {
        styleToApply = styles.prioritySpanInInputMedium;
      } else if (pKeyword === 'p3' || pKeyword === '!!3') {
        styleToApply = styles.prioritySpanInInputLow;
      }
    } else if (match.groups?.deadline) {
      styleToApply = styles.deadlineSpanInInput;
    } else if (match.groups?.target) {
      styleToApply = styles.targetSpanInInput;
    } else if (match.groups?.rrule) {
      styleToApply = styles.rruleSpanInInput;
    } else if (match.groups?.dueDate) {
      styleToApply = styles.dateSpanInInput;
    } else if (
      match.groups?.timeRange ||
      match.groups?.singleTime ||
      match.groups?.singleTimeAt
    ) {
      styleToApply = styles.startTimeSpanInInput;
    } else if (match.groups?.duration) {
      styleToApply = styles.durationSpanInInput;
    } else if (match.groups?.reminder) {
      styleToApply = styles.reminderSpanInInput;
    }

    Object.assign(span.style, styleToApply);

    // activeKeywordMatches already filters out ignored items, so we just make the rest clickable.
    if (Object.keys(styleToApply).length) {
      span.onclick = () => {
        desiredCursorPos.current = getCursorPosition(inputRef.current);
        const wordIndex = getWordIndexFromMatch(match, wordIds);
        const wordId = wordIds[wordIndex]?.id;
        onHighlightedWord(wordId);
      };
    }

    inputRef.current.appendChild(span);
    lastIndex = (match.index ?? -1) + fullMatch.length;
  }

  const remainingText = value.substring(lastIndex);
  if (remainingText) {
    inputRef.current.appendChild(document.createTextNode(remainingText));
  }

  if (inputRef.current.childNodes.length && currentCursorPos !== null) {
    const range = document.createRange();
    const sel = window.getSelection();
    const nodeIterator = document.createNodeIterator(
      inputRef.current,
      NodeFilter.SHOW_TEXT,
    );
    let charIndex = 0;
    let currentNode;
    // eslint-disable-next-line no-restricted-syntax -- NodeIterator has no functional iterator; a while loop is the natural traversal.
    while ((currentNode = nodeIterator.nextNode())) {
      const nodeLength = currentNode.textContent?.length ?? 0;
      if (charIndex + nodeLength >= currentCursorPos) {
        range.setStart(currentNode, currentCursorPos - charIndex);
        range.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(range);
        return;
      }
      charIndex += nodeLength;
    }
    range.selectNodeContents(inputRef.current);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  if (currentCursorPos !== null) {
    inputRef.current.focus();
  }
};
